/**
 * Meta Pixel + CAPI client helpers.
 *
 * - initPixel()        : runs once at app boot, sends the first PageView.
 * - trackPageView()    : fires on every route change.
 * - trackEvent(...)    : Pixel + CAPI mirror with a shared event_id (dedup).
 *
 * The Pixel base loader is injected in /public/index.html. We just call `fbq`
 * once it's available.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const _evtId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

let _initialized = false;
export function initPixel() {
  if (_initialized || !PIXEL_ID) return;
  if (typeof window === 'undefined' || !window.fbq) return;
  try {
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
    _initialized = true;
  } catch (e) { /* noop */ }
}

export function trackPageView() {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined' || !window.fbq) return;
  try { window.fbq('track', 'PageView'); } catch (e) { /* noop */ }
}

/**
 * trackEvent — fires Pixel (browser) AND server-side CAPI with a shared
 * event_id for deduplication.
 *
 * @param {string} name       Meta event name (Purchase, CompleteRegistration, etc.)
 * @param {object} params     Optional fields: { value, currency, content_name,
 *                            content_ids, email, phone, first_name, last_name,
 *                            city, country, custom_data }
 * @param {string} [eventId]  Optional pre-generated id (for server-known events)
 */
export function trackEvent(name, params = {}, eventId) {
  if (!PIXEL_ID) return;
  const id = eventId || _evtId(name.toLowerCase());
  const pixelParams = {};
  if (params.value != null) pixelParams.value = Number(params.value);
  if (params.currency) pixelParams.currency = String(params.currency).toUpperCase();
  if (params.content_name) pixelParams.content_name = params.content_name;
  if (params.content_ids) pixelParams.content_ids = params.content_ids;
  if (params.custom_data) Object.assign(pixelParams, params.custom_data);
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', name, pixelParams, { eventID: id });
    }
  } catch (e) { /* noop */ }
  // Mirror to CAPI (async, never blocks)
  try {
    axios.post(`${API}/meta/capi/event`, {
      event_name: name,
      event_id: id,
      event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
      email: params.email,
      phone: params.phone,
      first_name: params.first_name,
      last_name: params.last_name,
      city: params.city,
      country: params.country,
      value: params.value,
      currency: params.currency,
      content_name: params.content_name,
      content_ids: params.content_ids,
      custom_data: params.custom_data,
    }, { withCredentials: true }).catch(() => {});
  } catch (e) { /* noop */ }
}

/**
 * Auto-fires CompleteRegistration when the URL contains `?welcome=1`.
 * Idempotent per browser session (uses sessionStorage).
 */
export function trackWelcomeIfPresent() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') !== '1') return;
    if (sessionStorage.getItem('meta_welcome_fired') === '1') return;
    let email; let city;
    try {
      const u = JSON.parse(localStorage.getItem('nucleus_user') || 'null');
      if (u) { email = u.email; city = u.city; }
    } catch (e) { /* noop */ }
    trackEvent('CompleteRegistration', {
      email, city,
      content_name: 'School registration',
      custom_data: { user_type: 'School' },
    });
    sessionStorage.setItem('meta_welcome_fired', '1');
  } catch (e) { /* noop */ }
}
